import { useContext, useMemo } from 'react';

import MetricOptionsContext from 'contexts/MetricOptionsContext';
import { useBuildDateScope } from 'hooks/useDateScope';
import { getUsedMetrics } from 'hooks/useUsedMetrics';
import groupSeriesByOverrides from 'hooks/useV5ChartData/queryOverrides/groupSeriesByOverrides';
import toGroupFilterInput from 'hooks/useV5ChartData/queryOverrides/toGroupFilterInput';
import toQueryChartDef from 'hooks/useV5ChartData/queryOverrides/toQueryChartDef';
import { toSeriesName } from 'hooks/useV5ChartData/queryOverrides/describeQueryOverrides';
import SavedFiltersContext from 'screens/DataManager/DatasetFilters/SavedFilters/context/SavedFiltersContext';
import { usePlatesToFilterInput } from 'screens/GoalShow/useGoalData';

import { MatrixOverrides, hasOverrideSeries } from './index';

// What a matrix's own query fetches, and its columns that ask their own
// question (see MatrixOverrideGroup). The own query keeps every plain series;
// without any, the first overriding series' metric is carried along so it
// still finds the rows. Undefined overrides when there are none.
const useMatrixOverrides = ({
  chartDef,
  filterInput,
}: {
  chartDef: V5ChartDefinition;
  filterInput: FilterInput;
}): { queryChartDef: V5ChartDefinition; overrides?: MatrixOverrides } => {
  const platesToFilterInput = usePlatesToFilterInput();
  const buildDateScope = useBuildDateScope({});
  const { metricOptionsLookup } = useContext(MetricOptionsContext);
  const { savedFilters } = useContext(SavedFiltersContext);

  const split = useMemo(
    () =>
      hasOverrideSeries(chartDef)
        ? groupSeriesByOverrides(chartDef)
        : undefined,
    [chartDef],
  );

  const queryChartDef = useMemo(() => {
    if (!split) {
      return chartDef;
    }
    const rowCarrier: V5ChartDefinitionSeries[] =
      split.base.length > 0
        ? []
        : [
            {
              id: `${chartDef.id}-rows-${split.groups[0].series[0].metricId}`,
              metricId: split.groups[0].series[0].metricId,
            },
          ];
    return toQueryChartDef({
      chartDef,
      series: [...split.base, ...rowCarrier],
    });
  }, [chartDef, split]);

  const overrides = useMemo((): MatrixOverrides | undefined => {
    if (!split) {
      return undefined;
    }
    return {
      chartDef,
      groups: split.groups.map(({ key, overrides: query, series }) => {
        const { dateOffset, dateField } = query;
        const isOffset = !!dateOffset && dateOffset.count > 0;
        const groupChartDef = toQueryChartDef({ chartDef, series });
        return {
          key,
          series,
          chartDef: groupChartDef,
          filterInput: toGroupFilterInput({
            filterInput,
            overrides: query,
            platesToFilterInput,
          }),
          dateScope: buildDateScope({
            comparison: isOffset ? { compareType: dateOffset.type } : undefined,
            comparisonSteps: isOffset ? dateOffset.count : undefined,
            dateField,
          }),
          isOffset,
          usedMetrics: getUsedMetrics({
            series: groupChartDef.series,
            metricOptionsLookup,
          }).usedMetrics,
          seriesNames: Object.fromEntries(
            series.map((s) => [
              s.id,
              toSeriesName({
                metricName: metricOptionsLookup[s.metricId]?.name ?? '',
                series: s,
                savedFilters,
              }),
            ]),
          ),
        };
      }),
    };
  }, [
    buildDateScope,
    chartDef,
    filterInput,
    metricOptionsLookup,
    platesToFilterInput,
    savedFilters,
    split,
  ]);

  return { queryChartDef, overrides };
};

export default useMatrixOverrides;
